import { Component, Injector, Input, OnInit } from '@angular/core';
import { BaseModalComponent } from '@card-software/library';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';
import { LabDipRequestProxyService } from '../../../proxies/item/lab-dip-request.proxy.service';
import { FormControl } from '@angular/forms';
import { LabDip } from '../../../models/lab-dip/lab-dip';
import { LabDipProxyService } from '../../../proxies/item/lab-dip.proxy.service';
import { map, Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { Translation } from '../../../models/Translations';
import { TranslateService } from '@ngx-translate/core';

export interface LabDipFieldToDisplay extends LabDip {
  fieldToDisplay: string;
}

@Component({
  selector: 'app-color-lab-dips-management-modal',
  templateUrl: './color-lab-dips-management-modal.component.html',
  styleUrl: './color-lab-dips-management-modal.component.scss',
})
export class ColorLabDipsManagementModalComponent extends BaseModalComponent<string[]> implements OnInit {
  @Input() canEdit = false;
  @Input() colorId: string | null = null;
  @Input() colorName: string | null = null;
  @Input() labDipRequestId: string | null = null;
  @Input() labDipIds: FormControl<string[] | null> = new FormControl([]);

  isLoading = false;

  selectedLabDip: FormControl<LabDip | null> = new FormControl(null);

  translations: Record<string, Translation> = {};

  constructor(
    injector: Injector,
    private readonly labDipRequestProxyService: LabDipRequestProxyService,
    private readonly labDipProxyService: LabDipProxyService,
    private readonly messageService: MessageService,
    private translateService: TranslateService,
  ) {
    super(injector);
    this.translations = {
      Color_Lab_Dips_Modal_Title: {
        es: 'Gestionar Lab Dips de {{colorName}}',
        en: 'Manage {{colorName}} Lab Dips',
      },
      Color_Lab_Dips_Modal_Info: {
        es: 'Solo puedes asignar Lab Dips que no estén asignados a un color',
        en: 'You can only assign Lab Dips that are not assigned to a color',
      },
      Color_Lab_Dips_Modal_Lab_Dip_To_Add: {
        es: 'Lab Dip para Agregar',
        en: 'Lab Dip To Add',
      },
      Color_Lab_Dips_Modal_Cancel: {
        es: 'Cancelar',
        en: 'Cancel',
      },
      Success_Lab_Dip_Assigned: {
        es: 'Lab dip asignado al color exitosamente.',
        en: 'Lab dip assigned to color successfully.',
      },
    };
  }

  ngOnInit(): void {
    this.selectedLabDip.valueChanges.subscribe((labDip: LabDip | null) => {
      if (labDip && this.labDipRequestId && this.colorId && labDip.labDipNumber) {
        this.isLoading = true;
        this.labDipRequestProxyService.assignLabDipIdToColor(labDip.labDipNumber, this.labDipRequestId, this.colorId).subscribe({
          next: () => {
            this.isLoading = false;
            if (labDip.labDipNumber) {
              const labDipIds = this.labDipIds.value || [];
              this.labDipIds.setValue([...labDipIds, labDip.labDipNumber]);
              this.selectedLabDip.setValue(null);
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: this.getTranslation('Success_Lab_Dip_Assigned'),
              });
            }
          },
          error: () => {
            this.isLoading = false;
            this.selectedLabDip.setValue(null);
          },
        });
      }
    });
  }

  unassignLabDip(labDipId: string): void {
    if (this.labDipRequestId && this.colorId) {
      this.isLoading = true;
      this.labDipRequestProxyService.unassignLabDipIdFromColor(labDipId, this.labDipRequestId, this.colorId).subscribe({
        next: () => {
          this.isLoading = false;
          const labDipIds = this.labDipIds.value || [];
          this.labDipIds.setValue(labDipIds.filter((id) => id !== labDipId));
        },
        error: () => {
          this.isLoading = false;
        },
      });
    }
  }

  labDipTypeahead = (term: string): Observable<LabDipFieldToDisplay[]> => {
    const normalizedTerm = term.toLowerCase();
    const searchCommand = {
      labDipId: normalizedTerm,
      unassigned: true,
    };
    return this.labDipProxyService.searchLabDips(searchCommand).pipe(
      map((labDips: ApiResponse<PaginatedApiResponse<LabDip>>) => {
        return (labDips.data?.items || []).map((labDip: LabDip) => ({
          ...labDip,
          fieldToDisplay: labDip.labDipNumber || labDip.name,
        }));
      }),
    );
  };

  onLabDipClick(labDipId: string): void {
    const url = `/lab-dip-details?id=${labDipId}`;
    window.open(url, '_blank');
  }

  onClose(): void {
    this.close({
      action: 'close',
      data: this.labDipIds.value || [],
    });
  }

  getTranslation(key: string, params?: Record<string, string>): string {
    let translation = this.translations[key][this.translateService.currentLang as keyof Translation];
    if (params) {
      Object.keys(params).forEach((param) => {
        translation = translation.replace(`{{${param}}}`, params[param]);
      });
    }
    return translation;
  }
}
